import { CommonModule } from '@angular/common';
import { SafeDatePipe } from '../../../core/pipes/safe-date.pipe';
import { Component, DestroyRef, effect, inject, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	AsideLayoutComponent,
	FooterActionsComponent,
	ButtonComponent,
	ItemComponent,
	ItemGroupComponent,
	EmptyStateComponent,
	ProgressComponent,
	StatsComponent,
	TagComponent,
	RateAlertComponent,
	ProfileComponent,
	ButtonIconComponent,
	TabsComponent,
	ITab,
	TileComponent,
	FormGroupComponent,
	InputComponent,
	DividerComponent,
	DatepickerComponent,
	SelectComponent,
	FilePreviewComponent,
	InfoGroupComponent,
	IconComponent,
	ITagStats,
	AlertComponent,
	FilterComponent,
	LinkComponent,
	CommentsComponent,
	CheckboxComponent,
} from '@tecalisdev/infinity';
import {
	CHECK,
	IProcessDetailResponse,
	IProcessListBody,
	IProcessListPayload,
	IProcessListResponse,
	IProcessUpdatePayload,
	PROCESS_STATUS,
	TAB_PROCESS_REVIEW,
} from '../../models/processes.models';
import {
	collectOcrAdminImageUrls,
	firstOcrBackPreviewUrl,
	firstOcrFrontPreviewUrl,
} from '../../utils/ocr-admin-images.util';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, finalize, of } from 'rxjs';
import { OPERATION_TYPE } from '../../../operations/models/operations.models';
import { ProcessHelperService } from '../../services/process-helper.service';
import { ProcessService } from '../../services/process.service';
import { MasterService } from '../../../shared/services/master.service';
import { ActivatedRoute, Router } from '@angular/router';
import { IMetaResponse } from '../../../shared/models/master.models';
import { parseUserAgent } from '../../../core/utils/user-agent';
import { ReactiveFormsService } from '../../../shared/services/reactive-forms.service';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RolePermissionsService } from '../../../core/services/role-permissions.service';
import { UuidDisplayComponent } from '../../../shared/components/uuid-display/uuid-display.component';
import { ProcessDevicesTileDirective, ProcessDeviceBlockUnlockEvent } from '../../components/process-devices-tile/process-devices-tile.directive';
import { MapsLocalizationComponent } from '../../../shared/components/maps-localization/maps-localization.component';
import { ModalUserAgentComponent, DeviceUserAgentInput } from '../../components/modal-user-agent/modal-user-agent.component';
import { ChecksComponent } from '../../components/checks/checks.component';
import { DetailResultTileComponent } from '../../components/detail-result-tile/detail-result-tile.component';
import { ProcessChecksPanelsComponent } from '../../components/process-checks-panels/process-checks-panels.component';
import { ViewMediaService } from '../../../shared/services/view-media.service';
import { SnackbarService } from '../../../shared/services/snackbar.service';
import { NavigationService } from '../../../shared/services/navigation.service';
import { LoadingService } from '../../../shared/services/loading.service';
import { IIdentityBlockPayload } from '../../../identities/models/identities.models';
import { IdentitiesService } from '../../../identities/services/identities.service';
import { ModalRejectProcessComponent } from '../../components/modal-reject-process/modal-reject-process.component';
import { ModalBlockIdentityComponent } from '../../../shared/components/modal-block-identity/modal-block-identity.component';
import { ModalUnlockIdentityComponent } from '../../../shared/components/modal-unlock-identity/modal-unlock-identity.component';
import { UserActivityTrackerService } from '../../../shared/services/user-activity-tracker.service';
import { endOfLocalToday, notAvailableLabel, toCalendarDateString } from '../../../core/utils/utils';
import { ModalConfirmationService } from '../../../shared/services/modal-confirmation.service';

/** Sentinel ISO de documento sin caducidad (alineado con OCR / DNI ES permanente). */
const NO_EXPIRATION_DATE = '9999-01-01';

interface IUpdateIdentityThenApproveOptions {
	overwriteExisting?: boolean;
	confirmUnderageLegalAge?: boolean;
}

@Component({
	selector: 'app-process-review',
	imports: [
		CommonModule,
		SafeDatePipe,
		TranslatePipe,
		AsideLayoutComponent,
		FooterActionsComponent,
		ButtonComponent,
		ItemComponent,
		ItemGroupComponent,
		EmptyStateComponent,
		ProgressComponent,
		StatsComponent,
		TagComponent,
		RateAlertComponent,
		ProfileComponent,
		ButtonIconComponent,
		TabsComponent,
		TileComponent,
		FormGroupComponent,
		InputComponent,
		DividerComponent,
		DatepickerComponent,
		SelectComponent,
		CheckboxComponent,
		ReactiveFormsModule,
		FilePreviewComponent,
		InfoGroupComponent,
		UuidDisplayComponent,
		IconComponent,
		ProcessDevicesTileDirective,
		MapsLocalizationComponent,
		CommentsComponent,
		ModalUserAgentComponent,
		ChecksComponent,
		DetailResultTileComponent,
		ProcessChecksPanelsComponent,
		AlertComponent,
		ModalRejectProcessComponent,
		ModalBlockIdentityComponent,
		ModalUnlockIdentityComponent,
		FilterComponent,
		LinkComponent,
	],
	templateUrl: './process-review.component.html',
	styleUrl: './process-review.component.scss',
})
export class ProcessReviewComponent {
	public processHelper = inject(ProcessHelperService);
	public reactiveFormService = inject(ReactiveFormsService);
	private processService = inject(ProcessService);
	public masterService = inject(MasterService);
	private trans = inject(TranslateService);
	private router = inject(Router);
	private activatedRoute = inject(ActivatedRoute);
	public rolePermissions = inject(RolePermissionsService);
	private viewMedia = inject(ViewMediaService);
	private snackbarService = inject(SnackbarService);
	private navigationService = inject(NavigationService);
	private identitiesService = inject(IdentitiesService);
	private userActivityTracker = inject(UserActivityTrackerService);
	private modalConfirmationService = inject(ModalConfirmationService);
	private loading = inject(LoadingService);
	private destroyRef = inject(DestroyRef);

	public id: string;
	public meta: IMetaResponse;
	public detail: IProcessDetailResponse;
	public reviews: IProcessListBody[] = [];
	public reviewedCount: number = 0;
	public progressValue: number = 0;
	public tabs: ITab[] = [];
	public TAB_PROCESS_REVIEW = TAB_PROCESS_REVIEW;
	public CHECK = CHECK;
	public detailError: boolean = false;
	public loadingDetail: string = '';
	public loadingChecksTab = false;
	public loadingList: boolean = false;
	public errorAlert: string = '';
	public showModalReject: boolean = false;
	public showModalBlockIdentity = false;
	public showModalUnlockIdentity = false;
	public selectedOptionsIdentity: string[] = [];
	/** IP concreta del candado en tile Dispositivos; null = flujo genérico. */
	public selectedDeviceIp: string | null = null;
	public showReviews: boolean = false;
	public deviceForUserAgentModal: DeviceUserAgentInput | null = null;
	public form: FormGroup = new FormGroup({
		identifier: new FormControl('', Validators.required),
		nationality: new FormControl('', Validators.required),
		expiration_date: new FormControl('', Validators.required),
		expiration_permanent: new FormControl(false),
		team_number: new FormControl(''),
		support_number: new FormControl(''),
		birthdate: new FormControl('', Validators.required),
		gender: new FormControl(''),
		names: new FormControl('', Validators.required),
		surnames: new FormControl('', Validators.required),
		address: new FormControl(''),
		municipality: new FormControl(''),
		province: new FormControl(''),
	});
	public readonly birthdateMaxDate = endOfLocalToday();
	private page: number = 1;
	public allCountries = this.masterService.countries;
	/** Operaciones con payload full ya consumidas por Comprobaciones en review. */
	private checksOperationsHydrated = new Set<OPERATION_TYPE>();
	private checksTabLoaded = false;

	@ViewChild('video1') video1: any;
	@ViewChild('video2') video2: any;
	@ViewChild('video3') video3: any;

	private videoOffsets: { [key: number]: number } = {
		1: 0,
		2: 1 / 3,
		3: 2 / 3,
	};
	private videosReady: boolean = false;
	/** Fecha de caducidad previa al marcar Permanente (para restaurar al desmarcar). */
	private previousExpirationDate: unknown = '';

	constructor() {
		this.id = this.activatedRoute.snapshot.params['id'];
		this.getList();
		this.form
			.get('expiration_permanent')
			?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((isPermanent: boolean) => this.applyExpirationPermanentState(!!isPermanent));

		effect(() => {
			const modalResponse = this.modalConfirmationService.closeModal();
			if (!modalResponse) return;
			if (modalResponse.id === 'reassign-identity') {
				if (modalResponse.action === 'buttonPrimary') {
					this.updateIdentityThenApprove({ overwriteExisting: true });
				} else {
					this.loadingDetail = '';
				}
				return;
			}
			if (modalResponse.id === 'underage-legal-age') {
				if (modalResponse.action === 'buttonPrimary') {
					this.updateIdentityThenApprove({ confirmUnderageLegalAge: true });
				} else {
					this.loadingDetail = '';
				}
			}
		});
	}

	public getList(): void {
		this.loadingList = true;
		this.processService.list(this.getBody()).subscribe({
			next: (response: IProcessListResponse) => {
				if (this.reviews.length) {
					this.reviews = [...this.reviews, ...response.data];
				} else {
					this.reviews = response.data;
				}
				this.meta = response.meta;
				if (this.reviews.length) {
					this.getProcessDetail();
				}
			},
			error: (error: HttpErrorResponse) => {
				this.loadingList = false;
			},
			complete: () => {
				this.loadingList = false;
			},
		});
	}

	public getProcessDetail(): void {
		if (!this.id) {
			const firstReview = this.reviews.find((r: IProcessListBody) => r.status === PROCESS_STATUS.PENDING);
			if (firstReview) {
				this.id = firstReview.process_uuid;
			}
		}
		this.errorAlert = '';
		if (this.detail && this.id === this.detail.process_uuid) return;
		this.detailError = false;
		this.detail = undefined;
		this.checksOperationsHydrated.clear();
		this.checksTabLoaded = false;
		this.loadingChecksTab = false;
		this.showReviews = false;
		this.loadingDetail = this.trans.instant('LOADING.PROCESS_DETAIL');
		this.form.reset();
		this.processService.detail(this.id).subscribe({
			next: (response: IProcessDetailResponse) => {
				// Parse user agent
				if (response.general?.devices?.length) {
					response.general.devices.forEach((device) => {
						device.user_agent_info = parseUserAgent(device.user_agent);
					});
				}
				// Listado de imágenes completas
				response.images = [
					...collectOcrAdminImageUrls(response.data?.OCR?.images),
					...(response.data?.POL?.images?.selfie ?? []),
					...(response.data?.DOCUMENTS?.images?.document ?? []),
					...(this.rolePermissions.isSuperadmin() ? (response.data?.OCR?.images?.temporary ?? []) : []),
					...(this.rolePermissions.isSuperadmin() ? (response.data?.POL?.images?.temporary ?? []) : []),
				];

				this.detail = response;
				this.processHelper.setActivityLogs(this.detail);
				this.processHelper.setTimeline(this.detail);
				if (this.detail?.data?.OCR) {
					this.setForm();
					this.processHelper.checkExpirationDate(this.detail.data.OCR);
					this.processHelper.checkTypeDocument(this.detail.data.OCR);
					this.processHelper.checkCountry(this.detail.data.OCR);
					this.processHelper.setYearsOld(this.detail.data.OCR, this.detail?.identity?.birthdate);
				} else {
					this.processHelper.setYearsOld(null, this.detail?.identity?.birthdate);
				}
				if (this.detail?.data?.POL && this.processHelper.ocrDocumentAgeKnown) {
					this.processHelper.checkRangeAge(this.detail.data.POL);
				}

				this.setTabs();
				// Intentar inicializar videos si ya están disponibles
				this.waitForVideos();
				if (this.videosReady) {
					this.initVideos();
				}
			},
			error: (error: HttpErrorResponse) => {
				this.detailError = true;
				this.loadingDetail = '';
			},
			complete: () => {
				this.loadingDetail = '';
			},
		});
	}

	private getBody(): IProcessListPayload {
		const uuidPendingState = this.masterService.allStatus
			.find((s) => s.value === PROCESS_STATUS.PENDING)
			?.id?.toString();
		return {
			state_uuid: uuidPendingState,
			page: this.page,
			per_page: 15,
			sort_by: 'updated_at',
			sort_order: 'asc',
		};
	}

	public closeModalRejectProcess(payload: boolean | Partial<IIdentityBlockPayload>): void {
		this.showModalReject = false;
		if (payload && this.id) {
			this.rejectProcess(payload as Partial<IIdentityBlockPayload>);
		}
	}

	private rejectProcess(payload: Partial<IIdentityBlockPayload>): void {
		let reasons: string | undefined = undefined;
		if (payload.reasons && typeof payload.reasons === 'string') {
			const reasonsObject = JSON.parse(payload.reasons) as Record<string, boolean | string>;
			const reasonLabelById: Record<string, string> = {
				invalid_document: this.trans.instant('PROCESSES.BLOCK_IDENTITY_MODAL.INVALID_DOCUMENT'),
				document_data_not_valid: this.trans.instant('PROCESSES.BLOCK_IDENTITY_MODAL.DATA_REQUIREMENTS_NOT_MET'),
				expired_document: this.trans.instant('PROCESSES.BLOCK_IDENTITY_MODAL.EXPIRED_DOCUMENT'),
				damaged_or_unreadable_document: this.trans.instant('PROCESSES.BLOCK_IDENTITY_MODAL.DAMAGED_DOCUMENT'),
				front_and_back_mismatch: this.trans.instant('PROCESSES.BLOCK_IDENTITY_MODAL.FRONT_BACK_MISMATCH'),
				required_fields_not_readable: this.trans.instant(
					'PROCESSES.BLOCK_IDENTITY_MODAL.UNREADABLE_REQUIRED_FIELDS',
				),
				address_not_verifiable: this.trans.instant('PROCESSES.BLOCK_IDENTITY_MODAL.UNVERIFIABLE_ADDRESS'),
				document_photo_not_matching_face: this.trans.instant(
					'PROCESSES.BLOCK_IDENTITY_MODAL.PHOTO_FACE_MISMATCH',
				),
			};
			const labels: string[] = [];
			for (const [key, value] of Object.entries(reasonsObject)) {
				if (key === 'other_reason' && typeof value === 'string' && value.trim() !== '') {
					labels.push(value.trim());
				} else if (value === true && reasonLabelById[key]) {
					labels.push(reasonLabelById[key]);
				}
			}
			if (labels.length) {
				reasons = labels.join(', ');
			}
		}
		this.updateStatus(PROCESS_STATUS.KO, reasons);
		if (payload.blocked_ip || payload.blocked_identifier || payload.blocked_face) {
			this.identitiesService.blockIdentity(this.getBlockIdentityBody(payload)).subscribe({
				next: () => {},
				error: (error: HttpErrorResponse) => {},
				complete: () => {},
			});
		}
	}

	private getBlockIdentityBody(payload: Partial<IIdentityBlockPayload>): IIdentityBlockPayload {
		if (payload.blocked_ip === true || payload.blocked_ip === false) {
			if (this.selectedDeviceIp) {
				payload.ip = [this.selectedDeviceIp];
			} else if (payload.blocked_ip === true) {
				payload.ip = (this.detail.general.devices ?? [])
					.map((device) => device.ip)
					.filter((ip): ip is string => !!ip?.trim());
			} else {
				payload.ip = (this.detail.locks?.blocked_ips ?? [])
					.map((entry) => entry.ip)
					.filter((ip): ip is string => !!ip?.trim());
			}
		}

		if (payload.blocked_identifier === true || payload.blocked_identifier === false) {
			if (this.detail.identity.identifier) payload.identifier = this.detail.identity.identifier;
		}

		if (payload.blocked_face === true) {
			if (this.detail?.data?.POL && this.detail?.data?.POL?.data) {
				payload.operation_uuid = this.detail.data.POL.uuid;
			}
		}

		return {
			...payload,
			process_uuid: this.id,
			company_uuid: this.detail.general.company_uuid,
		};
	}

	/**
	 * Candado IP en tile Dispositivos (NIF-353): una IP concreta del dispositivo.
	 */
	public onDevicesBlockUnlock(event: ProcessDeviceBlockUnlockEvent): void {
		if (!event?.action || !event.device) {
			return;
		}
		this.selectedDeviceIp = event.device.ip?.trim() || null;
		this.selectedOptionsIdentity = ['ip'];
		if (event.action === 'block') {
			this.showModalBlockIdentity = true;
		} else {
			this.showModalUnlockIdentity = true;
		}
	}

	public closeBlockIdentity(payload: boolean | Partial<IIdentityBlockPayload>): void {
		this.showModalBlockIdentity = false;
		if (payload && typeof payload === 'object' && this.id) {
			this.blockIdentity(payload, false);
		} else {
			this.selectedDeviceIp = null;
		}
	}

	public closeUnlockIdentity(payload: boolean | Partial<IIdentityBlockPayload>): void {
		this.showModalUnlockIdentity = false;
		if (payload && typeof payload === 'object' && this.id) {
			this.blockIdentity(payload, true);
		} else {
			this.selectedDeviceIp = null;
		}
	}

	private blockIdentity(payload: Partial<IIdentityBlockPayload>, isUnlock: boolean): void {
		const loadingKey = isUnlock ? 'LOADING.UNLOCKING_IDENTITY' : 'LOADING.BLOCKING_IDENTITY';
		this.loading.show(this.trans.instant(loadingKey));

		this.identitiesService.blockIdentity(this.getBlockIdentityBody(payload)).subscribe({
			next: () => {
				this.loading.hide();
				this.selectedDeviceIp = null;
				this.forceReloadDetail();
			},
			error: (error: HttpErrorResponse) => {
				this.loading.hide();
				this.errorAlert = error?.error?.message || this.trans.instant('ERRORS.GENERIC_DESCRIPTION');
			},
		});
	}

	/** Fuerza recarga: getProcessDetail sale pronto si el uuid no cambia. */
	private forceReloadDetail(): void {
		this.detail = undefined;
		this.getProcessDetail();
	}

	public approveProcess(): void {
		if (this.detail?.data?.OCR && !this.form.valid) {
			this.form.markAllAsTouched();
			return;
		}
		// Aprobar confirma los datos del formulario (OCR); sincronizar siempre a Person,
		// aunque el form no esté dirty (OCR puede diferir de identity.names ya cargado).
		if (this.detail?.identity && this.detail?.data?.OCR) {
			this.updateIdentityThenApprove();
			return;
		}
		this.updateStatus(PROCESS_STATUS.OK, this.trans.instant('PROCESSES.REVIEW.APPROVE_MANUAL_REVIEW_REASON'));
	}

	private updateStatus(status: PROCESS_STATUS, reasons?: string): void {
		this.updateReviewedCount();
		let uuid: string = this.masterService.allStatus.find((s) => s.value === status)?.id?.toString();
		this.loadingDetail = this.trans.instant('LOADING.UPDATE_STATUS_PROCESS');
		this.processService.updateStatus(this.id, uuid, reasons, true).subscribe({
			next: (response: any) => {
				// Actualizamos el estado del proceso actual
				this.detail.status_process = status;
				const actualProcess = this.reviews.find((r: IProcessListBody) => r.process_uuid === this.id);
				if (actualProcess) actualProcess.status = status;
				this.snackbarService.show(
					this.trans.instant(
						`PROCESSES.REVIEW.${status === PROCESS_STATUS.OK ? 'APPROVE_PROCESS' : 'REJECT_PROCESS'}`,
					),
				);
				// Pasamos al siguiente proceso
				const nextReview = this.reviews.find((r: IProcessListBody) => r.status === PROCESS_STATUS.PENDING);
				if (nextReview) {
					this.id = nextReview.process_uuid;
					this.getProcessDetail();
				} else if (this.meta.current_page !== this.meta.last_page) {
					this.id = '';
					this.concatProcess();
				} else {
					this.reviews = [];
					this.loadingDetail = '';
				}
			},
			error: (error: HttpErrorResponse) => {
				this.loadingDetail = '';
				this.errorAlert = this.trans.instant('PROCESSES.REVIEW.UPDATE_PROCESS_ERROR');
			},
			complete: () => {},
		});
	}

	/**
	 * Persiste identidad (y OCR) antes de aprobar para no dejar el proceso OK si falla el PATCH person.
	 * Si el identifier ya existe → 409 + modal; al aceptar se reenvía con overwrite_existing.
	 * Si la edad queda por debajo del mínimo LEGAL_AGE → 409 + modal; al aceptar se reenvía con confirm_underage_legal_age.
	 */
	private updateIdentityThenApprove(options: IUpdateIdentityThenApproveOptions = {}): void {
		this.loadingDetail = this.trans.instant('LOADING.UPDATE_STATUS_PROCESS');
		this.errorAlert = '';
		const body: IProcessUpdatePayload = {
			...this.bodyUpdateDocumentData,
			...(options.overwriteExisting ? { overwrite_existing: true } : {}),
			...(options.confirmUnderageLegalAge ? { confirm_underage_legal_age: true } : {}),
		};
		this.processService.updatePersonOperation(this.detail.identity.uuid, body).subscribe({
			next: () => {
				this.updateStatus(
					PROCESS_STATUS.OK,
					this.trans.instant('PROCESSES.REVIEW.APPROVE_MANUAL_REVIEW_REASON'),
				);
			},
			error: (error: HttpErrorResponse) => {
				this.loadingDetail = '';
				if (error.status === 409 && error.error?.code === 'IDENTIFIER_EXISTS') {
					this.modalConfirmationService.show({
						id: 'reassign-identity',
						title: this.trans.instant('PROCESSES.REVIEW.REASSIGN_IDENTITY_TITLE'),
						description: this.trans.instant('PROCESSES.REVIEW.REASSIGN_IDENTITY_DESCRIPTION'),
						buttonPrimaryLabel: this.trans.instant('PROCESSES.REVIEW.REASSIGN_IDENTITY_ACCEPT'),
						buttonSecondaryLabel: this.trans.instant('BUTTON.CANCEL'),
						buttonPrimaryStyle: true,
					});
					return;
				}
				if (error.status === 409 && error.error?.code === 'LEGAL_AGE_BELOW_MINIMUM') {
					const yearsOld = Number(error.error?.data?.years);
					const legalAge = Number(error.error?.data?.legal_age);
					this.modalConfirmationService.show({
						id: 'underage-legal-age',
						title: this.trans.instant('PROCESSES.REVIEW.UNDERAGE_LEGAL_AGE_TITLE'),
						description: this.trans.instant('PROCESSES.REVIEW.UNDERAGE_LEGAL_AGE_DESCRIPTION', {
							yearsOld: Number.isFinite(yearsOld) ? yearsOld : 0,
							legalAge: Number.isFinite(legalAge) ? legalAge : 0,
						}),
						buttonPrimaryLabel: this.trans.instant('PROCESSES.REVIEW.UNDERAGE_LEGAL_AGE_ACCEPT'),
						buttonSecondaryLabel: this.trans.instant('BUTTON.CANCEL'),
						buttonPrimaryStyle: true,
					});
					return;
				}
				this.errorAlert = this.trans.instant('PROCESSES.REVIEW.UPDATE_IDENTITY_ERROR');
			},
		});
	}

	get bodyUpdateDocumentData(): IProcessUpdatePayload {
		// getRawValue: expiration_date puede estar disabled con Permanente (NIF-1012).
		const formData = this.form.getRawValue();
		const isPermanent = !!formData.expiration_permanent;
		const expirationDate = isPermanent
			? NO_EXPIRATION_DATE
			: this.asCalendarDate(formData.expiration_date);
		const operationsData = this.transformToKeyValueObject({
			...formData,
			expiration_date: expirationDate,
		});
		const sex = this.normalizeSex(formData.gender?.id ?? formData.gender);
		const payload: IProcessUpdatePayload = {
			names: formData.names,
			surnames: formData.surnames,
			identifier: formData.identifier,
			nationality: formData.nationality?.id ?? formData.nationality,
			expiration_date: expirationDate,
			birth_date: this.asCalendarDate(formData.birthdate),
			process_uuid: this.id,
			operation_uuid: this.detail.data.OCR.uuid,
			operations_data: operationsData,
			manual_validation: true,
		};
		if (sex) {
			payload.sex = sex;
		}
		return payload;
	}

	/** Solo envía sex si cumple el contrato API (M|F|Other). */
	private normalizeSex(value: unknown): string | undefined {
		if (value === 'M' || value === 'F' || value === 'Other') {
			return value;
		}
		return undefined;
	}

	/** Datepicker/`Date` o ISO con hora → `YYYY-MM-DD` (sin UTC que cambie el día). */
	private asCalendarDate(value: unknown): string {
		if (value instanceof Date) {
			return toCalendarDateString(value);
		}
		if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
			return value.slice(0, 10);
		}
		return typeof value === 'string' ? value : '';
	}

	private transformToKeyValueObject(data: any): any {
		const calendarDateKeys = new Set(['birthdate', 'expiration_date']);
		const skipKeys = new Set(['expiration_permanent']);
		const result: any = [];
		for (const key in data) {
			if (skipKeys.has(key)) {
				continue;
			}
			if (data[key] !== null && data[key] !== undefined) {
				let value = data[key]?.id ?? data[key];
				if (calendarDateKeys.has(key)) {
					value = this.asCalendarDate(value);
				}
				result.push({ key: key, value });
			}
		}
		return result;
	}

	/** Aplica sentinel OCR, disable del datepicker y quita/restaura required en caducidad. */
	private applyExpirationPermanentState(isPermanent: boolean): void {
		const expirationControl = this.form.get('expiration_date');
		if (!expirationControl) {
			return;
		}
		if (isPermanent) {
			const current = expirationControl.value;
			if (!this.isNoExpirationDate(current)) {
				this.previousExpirationDate = current;
			}
			expirationControl.setValue(NO_EXPIRATION_DATE, { emitEvent: false });
			expirationControl.clearValidators();
			// Disable vía FormControl (no solo `[disabled]` en plantilla): al hidratar
			// con Permanente ya ON, tec-datepicker no reaccionaba al Input inicial.
			expirationControl.disable({ emitEvent: false });
		} else {
			expirationControl.enable({ emitEvent: false });
			expirationControl.setValidators(Validators.required);
			if (this.isNoExpirationDate(expirationControl.value)) {
				expirationControl.setValue(this.previousExpirationDate ?? '', { emitEvent: false });
			}
			this.previousExpirationDate = '';
		}
		expirationControl.updateValueAndValidity({ emitEvent: false });
	}

	private isNoExpirationDate(value: unknown): boolean {
		const calendarDate = this.asCalendarDate(value);
		return calendarDate === NO_EXPIRATION_DATE;
	}

	public openUserAgentModal(device: DeviceUserAgentInput): void {
		this.deviceForUserAgentModal = device;
	}

	public closeUserAgentModal(): void {
		this.deviceForUserAgentModal = null;
	}

	public finishSession(): void {
		this.router.navigate(['processes']);
	}

	public selectProcess(review: IProcessListBody): void {
		if (this.loadingDetail) return;
		this.id = review.process_uuid;
		this.getProcessDetail();
	}

	public getRateAlertStatus(process: IProcessListBody): 'success' | 'error' | 'warning' | 'neutral' {
		if (process.status === PROCESS_STATUS.PENDING) return 'warning';
		if (process.status === PROCESS_STATUS.KO) return 'error';
		if (process.status === PROCESS_STATUS.OK) return 'success';
		return 'neutral';
	}

	public goToPanel(): void {
		this.router.navigate(['panel']);
	}

	private waitForVideos(): void {
		// Esperar a que los videos estén renderizados
		const checkVideos = setInterval(() => {
			if (this.video1?.nativeElement && this.video2?.nativeElement && this.video3?.nativeElement) {
				clearInterval(checkVideos);
				this.videosReady = true;
				console.log('Videos detectados y listos');
				this.initVideos();
			}
		}, 50);

		// Timeout de seguridad: detener la búsqueda después de 5 segundos
		setTimeout(() => clearInterval(checkVideos), 5000);
	}

	public concatProcess(): void {
		this.page++;
		this.getList();
	}

	private updateReviewedCount(): void {
		if (this.reviews.length) {
			this.reviewedCount = this.reviews.filter((review) => review.status !== PROCESS_STATUS.PENDING)?.length;
			this.progressValue = (this.reviewedCount * 100) / this.meta.total;
		}
		this.reviewedCount = 0;
		this.progressValue = 0;
	}

	private initVideos(): void {
		// Esperar a que los videos estén cargados
		if (!this.videosReady) {
			console.warn('Los videos aún no están listos');
			return;
		}

		if (this?.video1?.nativeElement) {
			this.video1.nativeElement.addEventListener('loadedmetadata', () => {
				const duration = this.video1.nativeElement.duration;
				this.video1.nativeElement.currentTime = duration * this.videoOffsets[1];
				if (this.video2?.nativeElement) this.video2.nativeElement.currentTime = duration * this.videoOffsets[2];
				if (this.video3?.nativeElement) this.video3.nativeElement.currentTime = duration * this.videoOffsets[3];
			});
		}
	}

	get tagRoundedByProcess(): ITagStats | null {
		if (this.detail?.general?.average_workFlow && this.detail?.general?.total_time != null) {
			const average =
				((this.detail.general.total_time - this.detail.general.average_workFlow) /
					this.detail.general.average_workFlow) *
				100;
			if (average < 0) {
				return { icon: 'icon-trending-up', label: Math.abs(average).toFixed(0) + '%' };
			} else if (average > 0) {
				return { icon: 'icon-trending-down', label: Math.abs(average).toFixed(0) + '%', styling: 'red' };
			}
		}
		return null;
	}

	private setForm(): void {
		const expirationDate = this.detail.data.OCR.expiration_date ?? '';
		const isPermanent = this.isNoExpirationDate(expirationDate);
		this.previousExpirationDate = '';
		this.form.patchValue(
			{
				identifier: this.detail.data.OCR.identifier ?? '',
				nationality: this.detail.data.OCR.nationality ?? '',
				expiration_date: expirationDate,
				expiration_permanent: isPermanent,
				team_number: this.detail.data.OCR.team_number ?? '',
				support_number: this.detail.data.OCR.support_number ?? '',
				birthdate: this.detail.data.OCR.birthdate ?? '',
				gender: this.detail.data.OCR.gender ?? '',
				names: this.detail.data.OCR.names ?? '',
				surnames: this.detail.data.OCR.surnames ?? '',
				address: this.detail.data.OCR.address ?? '',
				municipality: this.detail.data.OCR.municipality ?? '',
				province: this.detail.data.OCR.province ?? '',
			},
			{ emitEvent: false },
		);
		this.applyExpirationPermanentState(isPermanent);
	}

	/** NIF-1027: anverso coherente (ocr_preview o legacy). */
	get ocrFrontPreviewUrl(): string | undefined {
		return firstOcrFrontPreviewUrl(this.detail?.data?.OCR?.images);
	}

	/** NIF-1027: reverso coherente; undefined si unique / sin pareja. */
	get ocrBackPreviewUrl(): string | undefined {
		return firstOcrBackPreviewUrl(this.detail?.data?.OCR?.images);
	}

	public showViewMedia(imgSelected: string): void {
		if (!imgSelected || !this.detail?.images?.length) {
			return;
		}
		const reference = this.userActivityTracker.shortReference(this.detail?.process_uuid || this.id, 4, 2);
		this.viewMedia.setViewMedia({
			show: true,
			title: this.fullName,
			tag: {
				label: this.trans.instant('MASTER.STATUS.' + this.detail.status_process),
				status: this.masterService.getTagStatus(this.detail.status_process),
			},
			subtitle: this.masterService.getDocumentTypeLabel(this.detail?.data?.OCR?.document_type),
			activity: {
				type: 'processes',
				text: `Descarga de imágenes capturadas del proceso ${reference}`,
				link: this.userActivityTracker.buildLink(`/processes/detail/${this.id}`, reference),
				companyUuid: this.detail?.general?.company_uuid,
			},
			downloadFileName: `process_${this.id}_image.jpg`,
			data: this.detail.images.map((image: string) => ({
				type: 'image' as const,
				doc: image,
				selected: image === imgSelected,
				scale: 1.0,
			})),
		});
	}

	get fullName(): string {
		if (this.detail && this.detail?.identity?.names && this.detail?.identity?.surnames) {
			return (this.detail?.identity.names ?? '') + ' ' + (this.detail?.identity.surnames ?? '');
		}
		return notAvailableLabel(this.trans);
	}

	/** Navega al detalle del proceso (CTA «Ver proceso» del tile Comprobaciones). */
	public goToProcessDetail(event?: Event): void {
		const uuid = this.detail?.process_uuid;
		if (!uuid) return;
		this.navigationService.navigateTo({ routeSegments: ['processes', 'detail', uuid] }, event);
	}

	private setTabs(): void {
		if (!this.detail) return;
		this.tabs = [
			{
				id: TAB_PROCESS_REVIEW.OVERVIEW,
				label: this.trans.instant('PROCESSES.DETAIL.TABS.OVERVIEW'),
				selected: true,
				icon: 'icon-squares',
			},
		];
		if (this.detail?.data?.OCR && this.detail?.data?.POL) {
			this.tabs.push({
				id: TAB_PROCESS_REVIEW.OCR,
				label: this.trans.instant('PROCESSES.DETAIL.TABS.OCR'),
				selected: false,
				icon: 'icon-card',
			});
			this.tabs.push({
				id: TAB_PROCESS_REVIEW.POL,
				label: this.trans.instant('PROCESSES.DETAIL.TABS.POL'),
				selected: false,
				icon: 'icon-user',
			});
		}
		this.tabs.push(
			{
				id: TAB_PROCESS_REVIEW.IMAGES,
				label: this.trans.instant('PROCESSES.DETAIL.TABS.IMAGES'),
				selected: false,
				icon: 'icon-image',
			},
			{
				id: TAB_PROCESS_REVIEW.DETAILS,
				label: this.trans.instant('PROCESSES.DETAIL.TABS.DETAILS'),
				selected: false,
				icon: 'icon-capture-popup',
			},
		);
	}

	public tabSelected(tab: { id: string; label: string }): void {
		if (tab.id !== TAB_PROCESS_REVIEW.DETAILS || this.checksTabLoaded) {
			return;
		}
		this.loadChecksTab();
	}

	/** Hidrata ops de comprobaciones si faltan (mismo criterio que detalle). */
	private loadChecksTab(): void {
		const requiredKeys = this.getChecksRequiredOperationKeys();
		const missing = requiredKeys.filter(
			(key) => this.detail?.data?.[key] && !this.checksOperationsHydrated.has(key),
		);
		if (!missing.length) {
			this.runOcrPolLightChecks();
			this.checksTabLoaded = true;
			return;
		}

		this.loadingChecksTab = true;
		this.processService
			.detailOperations(this.id, missing)
			.pipe(
				catchError((error: HttpErrorResponse) => {
					this.errorAlert = error?.error?.message ?? this.trans.instant('GENERAL.ERROR');
					return of(null);
				}),
				finalize(() => {
					this.loadingChecksTab = false;
				}),
			)
			.subscribe((patch) => {
				if (!patch) {
					return;
				}
				this.detail = this.processHelper.mergeProcessDetail(this.detail, patch);
				missing.filter((key) => !!patch.data?.[key]).forEach((key) => this.checksOperationsHydrated.add(key));
				this.runOcrPolLightChecks();
				this.checksTabLoaded = true;
			});
	}

	private getChecksRequiredOperationKeys(): OPERATION_TYPE[] {
		const keys: OPERATION_TYPE[] = [];
		if (this.detail?.data?.OCR) {
			keys.push(OPERATION_TYPE.OCR);
		}
		if (this.detail?.data?.POL) {
			keys.push(OPERATION_TYPE.POL);
		}
		if (this.detail?.data?.DOCUMENTS) {
			keys.push(OPERATION_TYPE.DOCUMENTS);
		}
		if (this.detail?.data?.EVALUATION) {
			keys.push(OPERATION_TYPE.EVALUATION);
		}
		if (this.detail?.data?.ATTORNEY_POWERS) {
			keys.push(OPERATION_TYPE.ATTORNEY_POWERS);
		}
		return keys;
	}

	private runOcrPolLightChecks(): void {
		const ocr = this.detail?.data?.OCR;
		this.processHelper.setYearsOld(ocr, this.detail?.identity?.birthdate);
		if (ocr?.allowed_docs && ocr.antifraud) {
			this.processHelper.checkExpirationDate(ocr);
			this.processHelper.checkTypeDocument(ocr);
			this.processHelper.checkCountry(ocr);
		}
		if (this.detail?.data?.POL && this.processHelper.ocrDocumentAgeKnown) {
			this.processHelper.checkRangeAge(this.detail.data.POL);
		}
	}
}
